import React, { useState, useEffect } from 'react';
import { MoreHorizontal, Check, X, Trash2, Eye, MessageCircle, Shield, Loader2 } from 'lucide-react';
import { nohellocian } from '../../assets/assets';
import { 
    useGetClients, 
    useGetTrashedClients, 
    useToggleBlockClient, 
    useDeleteClient, 
    useRestoreClient, 
    useHardDeleteClient, 
    useBulkDeleteClients 
} from '../../services/auth/auth';
import { toast } from 'react-toastify';

const ClientsDataTable = () => {
    const [view, setView] = useState('active'); // 'active' | 'trash'
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    const { mutateAsync: fetchActive } = useGetClients();
    const { mutateAsync: fetchTrashed } = useGetTrashedClients();
    const { mutateAsync: toggleBlock } = useToggleBlockClient();
    const { mutateAsync: deleteClient } = useDeleteClient();
    const { mutateAsync: restoreClient } = useRestoreClient();
    const { mutateAsync: hardDeleteClient } = useHardDeleteClient();
    const { mutateAsync: bulkDelete } = useBulkDeleteClients();

    const loadClients = async () => {
        setIsLoading(true);
        try {
            const response = await (view === 'active' ? fetchActive() : fetchTrashed());
            setClients(response.clients.map(c => ({ ...c, selected: false })));
        } catch (error) {
            toast.error('Failed to load clients');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadClients();
    }, [view]);

    const [showHeaderDropdown, setShowHeaderDropdown] = useState(false);
    const [showRowDropdown, setShowRowDropdown] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [blockReason, setBlockReason] = useState("");
    const [isActioning, setIsActioning] = useState(false);

    const toggleClientSelection = (clientId) => {
        setClients(clients.map(client =>
            client._id === clientId ? { ...client, selected: !client.selected } : client
        ));
    };

    const selectAllClients = () => {
        const allSelected = clients.every(client => client.selected);
        setClients(clients.map(client => ({ ...client, selected: !allSelected })));
    };

    const handleDelete = async (clientId) => {
        try {
            setIsActioning(true);
            if (view === 'active') {
                await deleteClient(clientId);
                toast.success("Client moved to trash");
            } else {
                await hardDeleteClient(clientId);
                toast.success("Client permanently deleted");
            }
            setClients(clients.filter(c => c._id !== clientId));
            closeModal();
        } catch (error) {
            toast.error("Failed to delete client");
        } finally {
            setIsActioning(false);
        }
    };

    const handleRestore = async (clientId) => {
        try {
            setIsActioning(true);
            await restoreClient(clientId);
            toast.success("Client restored successfully");
            setClients(clients.filter(c => c._id !== clientId));
            closeModal();
        } catch (error) {
            toast.error("Failed to restore client");
        } finally {
            setIsActioning(false);
        }
    };

    const handleBulkAction = async () => {
        const selectedIds = clients.filter(c => c.selected).map(c => c._id);
        if (selectedIds.length === 0) return;
        
        try {
            setIsActioning(true);
            await bulkDelete({ ids: selectedIds, action: view === 'active' ? 'soft' : 'hard' });
            toast.success(`${selectedIds.length} clients ${view === 'active' ? 'moved to trash' : 'permanently deleted'}`);
            setClients(clients.filter(c => !c.selected));
            setShowHeaderDropdown(false);
        } catch (error) {
            toast.error("Failed to process bulk action");
        } finally {
            setIsActioning(false);
        }
    };

    const handleToggleBlock = async () => {
        if (!selectedClient) return;
        try {
            setIsActioning(true);
            await toggleBlock({ id: selectedClient._id, reason: blockReason });
            toast.success(`Client ${selectedClient.isBlocked ? 'unblocked' : 'blocked'} successfully`);
            
            // Update local state
            setClients(clients.map(c => c._id === selectedClient._id ? { ...c, isBlocked: !c.isBlocked } : c));
            closeModal();
        } catch (error) {
            toast.error("Failed to update status");
        } finally {
            setIsActioning(false);
        }
    };

    const openModal = (action, client) => {
        setSelectedClient({ ...client, action });
        setBlockReason("");
        setShowModal(true);
        setShowRowDropdown(null);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedClient(null);
        setBlockReason("");
    };

    const CustomCheckbox = ({ checked, onChange, indeterminate = false }) => (
        <div
            className={`w-5 h-5 border-2 rounded cursor-pointer flex items-center justify-center transition-colors ${
                checked ? 'bg-[#4B718F] border-[#4B718F]' : 'border-gray-300 dark:border-[#32353c] bg-white dark:bg-[#1d2026]'
            } ${indeterminate ? 'bg-[#4B718F] border-[#4B718F]' : ''}`}
            onClick={onChange}
        >
            {(checked || indeterminate) && (
                <Check className="w-3 h-3 text-white" />
            )}
        </div>
    );

    const StatusBadge = ({ isActivated, isBlocked }) => {
        if (isBlocked) {
            return <span className="px-3 py-1 rounded-full text-sm font-medium border-[1px] border-red-500 dark:border-red-500/50 text-red-500 dark:text-red-400">Blocked</span>;
        }
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                isActivated ? 'border-[1px] border-[#1FA860] text-[#1FA860] dark:text-green-400 dark:border-green-500/50' : 'border-[1px] border-yellow-500 text-yellow-500 dark:text-yellow-400 dark:border-yellow-500/50'
            }`}>
                {isActivated ? 'Activated' : 'Pending'}
            </span>
        );
    };

    const selectedCount = clients.filter(client => client.selected).length;
    const isIndeterminate = selectedCount > 0 && selectedCount < clients.length;
    const allSelected = selectedCount === clients.length && clients.length > 0;

    if (isLoading) {
        return (
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#174568]" />
            </div>
        );
    }

    if (clients.length === 0 && !isLoading) {
        return (
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-[#32353c] flex items-center gap-4">
                    <button onClick={() => setView('active')} className={`text-sm font-medium pb-2 ${view === 'active' ? 'text-[#4B718F] border-b-2 border-[#4B718F]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>Active</button>
                    <button onClick={() => setView('trash')} className={`text-sm font-medium pb-2 ${view === 'trash' ? 'text-[#4B718F] border-b-2 border-[#4B718F]' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>Trash</button>
                </div>
                <div className="p-8 text-center flex flex-col gap-3">
                    <img src={nohellocian} alt='no hellocian' className='mx-auto w-[131px]' />
                    <p className="text-gray-500 dark:text-gray-400">There are no clients here at the moment.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-[#32353c] flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => setView('active')} className={`text-lg font-semibold ${view === 'active' ? 'text-[#4B718F] dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>Active</button>
                        <button onClick={() => setView('trash')} className={`text-lg font-semibold ${view === 'trash' ? 'text-[#4B718F] dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>Trash</button>
                    </div>
                    <div className="relative">
                        <button
                            onClick={() => setShowHeaderDropdown(!showHeaderDropdown)}
                            className="p-1 bg-[#F4F7FE] dark:bg-[#1d2026] rounded-full"
                        >
                            <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
                        </button>

                        {showHeaderDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-10">
                                <button
                                    onClick={selectAllClients}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                >
                                    <Check className="w-4 h-4" />
                                    {allSelected ? 'Deselect All' : 'Select All'}
                                </button>
                                <button
                                    onClick={handleBulkAction}
                                    disabled={selectedCount === 0 || isActioning}
                                    className="w-full px-4 py-2 text-left text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {view === 'active' ? 'Move to Trash' : 'Delete Permanently'} ({selectedCount})
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#1d2026]/60">
                            <tr>
                                <th className="px-6 py-3 text-left">
                                    <CustomCheckbox
                                        checked={allSelected}
                                        indeterminate={isIndeterminate}
                                        onChange={selectAllClients}
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Full Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Username</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Orders</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Spent</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider text-nowrap">Date Added</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#A3AED0] uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-[#141414] divide-y divide-gray-200 dark:divide-gray-800">
                            {clients.map((client) => (
                                <tr key={client._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                                    <td className="px-6 py-4">
                                        <CustomCheckbox
                                            checked={client.selected}
                                            onChange={() => toggleClientSelection(client._id)}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {client.firstName} {client.lastName}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {client.username || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4B718F] dark:text-blue-400">
                                        {client.orderCount || 0}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-medium">
                                        ${(client.totalSpent || 0).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <StatusBadge isActivated={client.isActivated} isBlocked={client.isBlocked} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="relative">
                                            <button
                                                onClick={() => setShowRowDropdown(showRowDropdown === client._id ? null : client._id)}
                                                className="p-1 bg-[#F4F7FE] dark:bg-[#1d2026] rounded-full"
                                            >
                                                <MoreHorizontal className="w-5 h-5 text-[#4318FF]" />
                                            </button>

                                            {showRowDropdown === client._id && (
                                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-lg z-10">
                                                    {view === 'active' ? (
                                                        <>
                                                            <button
                                                                onClick={() => openModal('view', client)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                View Details
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('message', client)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                                            >
                                                                <MessageCircle className="w-4 h-4" />
                                                                Message
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('block', client)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 dark:text-gray-300"
                                                            >
                                                                <Shield className="w-4 h-4" />
                                                                {client.isBlocked ? 'Unblock' : 'Block'}
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('delete', client)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-red-600 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Move to Trash
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => openModal('restore', client)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 text-green-600 dark:text-green-400"
                                                            >
                                                                <Check className="w-4 h-4" />
                                                                Restore
                                                            </button>
                                                            <button
                                                                onClick={() => openModal('delete', client)}
                                                                className="w-full px-4 py-2 text-left hover:bg-gray-50 text-red-600 flex items-center gap-2"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                                Delete Permanently
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Modal */}
                 {showModal && selectedClient && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-[#0b0e14] rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {selectedClient.action === 'view' && 'Client Details'}
                                    {selectedClient.action === 'message' && 'Send Message'}
                                    {selectedClient.action === 'block' && (selectedClient.isBlocked ? 'Unblock Client' : 'Block Client')}
                                    {selectedClient.action === 'delete' && (view === 'active' ? 'Move to Trash' : 'Delete Permanently')}
                                    {selectedClient.action === 'restore' && 'Restore Client'}
                                </h3>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="mb-6">
                                 {selectedClient.action === 'view' && (
                                    <div className="space-y-3 dark:text-gray-300">
                                        <p><span className="font-medium text-gray-900 dark:text-white">Name:</span> {selectedClient.firstName} {selectedClient.lastName}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Username:</span> {selectedClient.username || 'N/A'}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Email:</span> {selectedClient.email}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Phone:</span> {selectedClient.phone || 'N/A'}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Orders:</span> {selectedClient.orderCount || 0}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Total Spent:</span> ${selectedClient.totalSpent || 0}</p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Status:</span> <StatusBadge isActivated={selectedClient.isActivated} isBlocked={selectedClient.isBlocked} /></p>
                                        <p><span className="font-medium text-gray-900 dark:text-white">Member Since:</span> {selectedClient.createdAt ? new Date(selectedClient.createdAt).toLocaleDateString() : 'N/A'}</p>
                                    </div>
                                )}

                                {selectedClient.action === 'message' && (
                                    <div>
                                        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">Send a message to {selectedClient.firstName}</p>
                                        <textarea
                                            className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#4B718F]"
                                            rows="4"
                                            placeholder="Type your message here..."
                                        />
                                    </div>
                                )}

                                 {selectedClient.action === 'block' && (
                                    <div>
                                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                                            Are you sure you want to {selectedClient.isBlocked ? 'unblock' : 'block'} <span className="font-medium text-gray-900 dark:text-white">{selectedClient.firstName} {selectedClient.lastName}</span>?
                                            {!selectedClient.isBlocked && " They will lose access to their account features."}
                                        </p>
                                        {!selectedClient.isBlocked && (
                                            <div>
                                                <label className="text-sm text-gray-600 dark:text-gray-400 mb-1 block">Reason (Optional)</label>
                                                <textarea
                                                    className="w-full p-2 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg resize-none text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                                    rows="3"
                                                    placeholder="Reason for blocking..."
                                                    value={blockReason}
                                                    onChange={e => setBlockReason(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {selectedClient.action === 'delete' && (
                                    <div>
                                        <p className="text-gray-700 dark:text-gray-300">
                                            Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">{selectedClient.firstName} {selectedClient.lastName}</span>?
                                            {view === 'active' ? ' They will be moved to trash.' : ' This action is permanent and cannot be undone.'}
                                        </p>
                                    </div>
                                )}

                                {selectedClient.action === 'restore' && (
                                    <div>
                                        <p className="text-gray-700 dark:text-gray-300">
                                            Are you sure you want to restore <span className="font-medium text-gray-900 dark:text-white">{selectedClient.firstName} {selectedClient.lastName}</span>?
                                            They will regain full access to their account.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={closeModal}
                                    disabled={isActioning}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (selectedClient.action === 'view') closeModal();
                                        if (selectedClient.action === 'block') handleToggleBlock();
                                        if (selectedClient.action === 'delete') handleDelete(selectedClient._id);
                                        if (selectedClient.action === 'restore') handleRestore(selectedClient._id);
                                    }}
                                    disabled={isActioning}
                                    className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
                                        selectedClient.action === 'block' || selectedClient.action === 'delete'
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-[#4B718F] hover:bg-[#3a5a75]'
                                    }`}
                                >
                                    {isActioning ? 'Processing...' : (
                                        <>
                                            {selectedClient.action === 'view' && 'Close'}
                                            {selectedClient.action === 'message' && 'Send Message'}
                                            {selectedClient.action === 'block' && (selectedClient.isBlocked ? 'Unblock Client' : 'Block Client')}
                                            {selectedClient.action === 'delete' && (view === 'active' ? 'Move to Trash' : 'Delete Permanently')}
                                            {selectedClient.action === 'restore' && 'Restore Client'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Click outside handler */}
                {(showHeaderDropdown || showRowDropdown) && (
                    <div
                        className="fixed inset-0 z-5"
                        onClick={() => {
                            setShowHeaderDropdown(false);
                            setShowRowDropdown(null);
                        }}
                    />
                )}
            </div>
        </>
    );
};

export default ClientsDataTable;